import { useMemo, useRef, useState } from 'react';
import { isElevatedRole } from '@/lib/roles';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  Loader2,
  Play,
  RefreshCw,
  Square,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { useTenantStore } from '@/stores/useTenantStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import {
  useCancelResearchCoverageJob,
  useResearchCoverageImports,
  useResearchCoverageJob,
  useResearchCoverageJobs,
  useResearchCoveragePools,
  useTriggerResearchCoverageJob,
} from '@/hooks/useResearchCoverage';
import type {
  ResearchPool,
  ResearchTriggerJobRequest,
  ResearchCoverageJobSummary,
} from '@/services/researchCoverageService';
import {
  getResearchCoverageErrorMessage,
  isActiveResearchCoverageJobStatus,
} from '@/services/researchCoverageService';

function formatDateTime(value: string | null | undefined) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function parseLines(value: string) {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function makeIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) {
    return `research-${globalThis.crypto.randomUUID()}`;
  }
  return `research-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function statusBadge(status: string) {
  switch (status) {
    case 'queued':
      return (
        <Badge variant="outline" className="gap-1">
          <Clock className="h-3 w-3" /> Queued
        </Badge>
      );
    case 'running':
      return (
        <Badge className="gap-1 bg-teal-light text-teal-dark border-teal/20">
          <Activity className="h-3 w-3" /> Finding sources
        </Badge>
      );
    case 'succeeded':
      return (
        <Badge className="gap-1 bg-teal-light text-teal-dark border-teal/20">
          <CheckCircle2 className="h-3 w-3" /> Done
        </Badge>
      );
    case 'partial':
      return (
        <Badge className="gap-1 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800">
          <AlertTriangle className="h-3 w-3" /> Partly done
        </Badge>
      );
    case 'failed':
      return (
        <Badge className="gap-1 bg-destructive/10 text-destructive border-destructive/20">
          <XCircle className="h-3 w-3" /> Failed
        </Badge>
      );
    case 'cancelled':
      return (
        <Badge variant="secondary" className="gap-1">
          <Square className="h-3 w-3" /> Cancelled
        </Badge>
      );
    case 'cancel_requested':
      return (
        <Badge className="gap-1 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800">
          <Clock className="h-3 w-3" /> Cancelling…
        </Badge>
      );
    default:
      return <Badge variant="outline">Unknown</Badge>;
  }
}

function keywordStatusLabel(status: string) {
  switch (status) {
    case 'pending':
      return 'Waiting';
    case 'running':
      return 'Searching';
    case 'done':
      return 'Done';
    case 'failed':
      return 'Failed';
    case 'skipped':
      return 'Skipped';
    default:
      return 'Unknown';
  }
}

function ElevatedGuard({ children }: { children: React.ReactNode }) {
  const { userProfile } = useWorkspaceStore();
  const role = userProfile?.platform_role;
  if (!isElevatedRole(role)) {
    return (
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="text-xl font-semibold">Admin Access Required</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Research Coverage is available to platform admins only.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

function getPoolStatus(pool: ResearchPool) {
  if (pool.readiness === 'ready') return 'ready';
  if (pool.usable_rows > 0) return 'thin';
  return 'missing';
}

function poolStatusBadge(pool: ResearchPool) {
  const status = getPoolStatus(pool);
  if (status === 'ready') {
    return (
      <Badge className="bg-teal-light text-teal-dark border-teal/20">
        Ready
      </Badge>
    );
  }
  if (status === 'thin') {
    return (
      <Badge className="bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800">
        Thin
      </Badge>
    );
  }
  return (
    <Badge className="bg-coral/10 text-coral border-coral/20">
      Needs research
    </Badge>
  );
}

function ClientSummaryCards({ tenantId }: { tenantId: string }) {
  const pools = useResearchCoveragePools(tenantId);
  const imports = useResearchCoverageImports(tenantId, 10);
  const jobs = useResearchCoverageJobs({ tenant_id: tenantId, limit: 10 });

  const poolRows = pools.data?.pools ?? [];
  const approvedSources = poolRows.reduce((total, pool) => total + pool.usable_rows, 0);
  const readyKeywords = poolRows.filter((pool) => getPoolStatus(pool) === 'ready').length;
  const needsResearch = poolRows.filter((pool) => getPoolStatus(pool) !== 'ready').length;
  const lastImport = imports.data?.imports[0]?.timestamp ?? null;
  const lastJob = jobs.data?.jobs[0];

  if (pools.isLoading || imports.isLoading || jobs.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading this client's research coverage...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-4">
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Approved sources</CardDescription>
          <CardTitle className="text-2xl">{approvedSources}</CardTitle>
          <p className="text-xs text-muted-foreground">
            Ready to cite for {tenantId}
          </p>
        </CardHeader>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Keywords ready</CardDescription>
          <CardTitle className="text-2xl">{readyKeywords}</CardTitle>
          <p className="text-xs text-muted-foreground">
            {poolRows.length ? `${poolRows.length} tracked keywords` : 'No tracked keywords yet'}
          </p>
        </CardHeader>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Need more research</CardDescription>
          <CardTitle className="text-2xl">{needsResearch}</CardTitle>
          <p className="text-xs text-muted-foreground">
            Thin or missing approved source pools
          </p>
        </CardHeader>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Last activity</CardDescription>
          <CardTitle className="text-sm">
            {lastJob ? statusBadge(lastJob.status) : 'No requests yet'}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {lastJob?.created_at ? formatDateTime(lastJob.created_at) : lastImport ? formatDateTime(lastImport) : 'No sources added yet'}
          </p>
        </CardHeader>
      </Card>
    </div>
  );
}

interface KeywordResearchRequest {
  id: number;
  keyword: string;
}

function TriggerCard({
  tenantId,
  keywordRequest,
}: {
  tenantId: string;
  keywordRequest: KeywordResearchRequest | null;
}) {
  const [mode, setMode] = useState<'feed' | 'manual'>('feed');
  const [keywords, setKeywords] = useState('');
  // Initialized to the current request id so a request left over from a
  // previous client (this card remounts per tenant) is never applied.
  const [appliedKeywordRequestId, setAppliedKeywordRequestId] = useState(keywordRequest?.id ?? 0);
  const [feedItemIds, setFeedItemIds] = useState('');
  const [targetSources, setTargetSources] = useState('12');
  const [maxRounds, setMaxRounds] = useState('10');
  const [allowCandidates, setAllowCandidates] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const normalizedTenant = tenantId.trim().toLowerCase();
  const triggerJob = useTriggerResearchCoverageJob();
  // Same params as ClientSummaryCards/JobsCard so all three share one polled query.
  const tenantJobs = useResearchCoverageJobs({ tenant_id: normalizedTenant, limit: 10 });

  const activeJob = tenantJobs.data?.jobs.find(
    (job) => isActiveResearchCoverageJobStatus(job.status),
  );

  // Render-time adjustment (not an effect): prefill the form when a
  // "Find sources" click in Keyword Coverage hands us a new request.
  if (keywordRequest && keywordRequest.id !== appliedKeywordRequestId) {
    setAppliedKeywordRequestId(keywordRequest.id);
    setMode('manual');
    setKeywords(keywordRequest.keyword);
  }

  async function submit() {
    if (!normalizedTenant) {
      toast.error('Choose a client first.');
      return;
    }

    const request: ResearchTriggerJobRequest = {
      tenant_id: normalizedTenant,
      target_sources: Math.max(1, Number(targetSources) || 12),
      max_rounds: Math.max(1, Number(maxRounds) || 10),
      candidate_policy: allowCandidates ? 'allow_candidate' : 'approved_only',
      dry_run: dryRun,
    };

    if (mode === 'manual') {
      const parsedKeywords = parseLines(keywords);
      if (!parsedKeywords.length) {
        toast.error('Add at least one keyword to research.');
        return;
      }
      request.keywords = parsedKeywords;
    } else {
      const parsedFeedIds = parseLines(feedItemIds);
      if (parsedFeedIds.length) {
        request.feed_item_ids = parsedFeedIds;
      } else {
        request.select = {
          status: allowCandidates ? ['approved', 'candidate'] : ['approved'],
          limit_per_client: 30,
        };
      }
    }

    try {
      const job = await triggerJob.mutateAsync({
        request,
        idempotencyKey: makeIdempotencyKey(),
      });
      toast.success(`Research request started for ${normalizedTenant}. Reference: ${job.job_id}`);
    } catch (error) {
      toast.error(getResearchCoverageErrorMessage(error));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Play className="h-4 w-4 text-coral" />
          Find More Sources
        </CardTitle>
        <CardDescription>
          Grow the approved source pool for {normalizedTenant}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
          <span className="text-muted-foreground">Workspace:</span>{' '}
          <span className="font-medium">{normalizedTenant}</span>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Button
            type="button"
            size="sm"
            variant={mode === 'feed' ? 'default' : 'outline'}
            onClick={() => setMode('feed')}
          >
            From this client's keywords
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === 'manual' ? 'default' : 'outline'}
            onClick={() => setMode('manual')}
          >
            From specific keywords
          </Button>
        </div>

        {mode === 'manual' && (
          <div className="space-y-2">
            <Label htmlFor="research-keywords">Keywords to research</Label>
            <Textarea
              id="research-keywords"
              value={keywords}
              onChange={(event) => setKeywords(event.target.value)}
              placeholder="One keyword per line"
              rows={4}
            />
          </div>
        )}

        <div className="border-t pt-3">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            {showAdvanced ? 'Hide' : 'Show'} advanced options
          </button>
          {showAdvanced && (
            <div className="mt-3 space-y-4">
              <p className="text-xs text-muted-foreground">
                Defaults work for most clients. Only change these if you know what you need.
              </p>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="research-target">Sources to find (target)</Label>
                  <Input
                    id="research-target"
                    type="number"
                    min={1}
                    value={targetSources}
                    onChange={(event) => setTargetSources(event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="research-rounds">Search depth (max rounds)</Label>
                  <Input
                    id="research-rounds"
                    type="number"
                    min={1}
                    value={maxRounds}
                    onChange={(event) => setMaxRounds(event.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allowCandidates}
                    onChange={(event) => setAllowCandidates(event.target.checked)}
                  />
                  Include unverified sources
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(event) => setDryRun(event.target.checked)}
                  />
                  Preview only — don't save results
                </label>
              </div>

              {allowCandidates && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Heads up: this will include sources that haven't been fully verified
                    yet. Only turn this on if rougher, provisional sources are okay for this
                    client.
                  </span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="research-feed-items">Specific feed items (optional)</Label>
                <Textarea
                  id="research-feed-items"
                  value={feedItemIds}
                  onChange={(event) => setFeedItemIds(event.target.value)}
                  placeholder="Optional — paste feed item IDs, one per line. Leave blank to use this workspace's approved keywords."
                  rows={3}
                />
              </div>
            </div>
          )}
        </div>

        {activeJob && (
          <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
            This client already has a research request running. Reference:{' '}
            <span className="font-mono text-xs">{activeJob.job_id}</span>
          </div>
        )}

        <Button
          type="button"
          onClick={submit}
          disabled={triggerJob.isPending || Boolean(activeJob)}
          className="gap-2"
        >
          {triggerJob.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {triggerJob.isPending ? 'Finding sources…' : 'Find Sources'}
        </Button>
        {triggerJob.isError && (
          <div className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {getResearchCoverageErrorMessage(triggerJob.error)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function JobsCard({
  tenantId,
  title,
  description,
  showTenant = true,
}: {
  tenantId: string;
  title?: string;
  description?: string;
  showTenant?: boolean;
}) {
  const jobs = useResearchCoverageJobs({ tenant_id: tenantId, limit: 10 });
  const cancelJob = useCancelResearchCoverageJob();

  async function cancel(job: ResearchCoverageJobSummary) {
    try {
      await cancelJob.mutateAsync({ jobId: job.job_id, tenantId: job.tenant_id });
      toast.success('Cancelling this research request…');
    } catch (error) {
      toast.error(getResearchCoverageErrorMessage(error));
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">{title ?? 'Recent Research Requests'}</CardTitle>
            <CardDescription>
              {description ?? 'Your most recent requests to find more sources.'}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => jobs.refetch()}
            disabled={jobs.isFetching}
            aria-label="Refresh research requests"
          >
            <RefreshCw className={`h-4 w-4 ${jobs.isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {jobs.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading research requests…
          </div>
        ) : jobs.isError ? (
          <div className="text-sm text-destructive">{getResearchCoverageErrorMessage(jobs.error)}</div>
        ) : !jobs.data?.jobs.length ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            No research requests for {tenantId} yet. Use "Find More Sources" above to start one.
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.data.jobs.map((job) => (
              <JobRow
                key={job.job_id}
                job={job}
                onCancel={cancel}
                cancelling={cancelJob.isPending}
                showTenant={showTenant}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function JobRow({
  job,
  onCancel,
  cancelling,
  showTenant = true,
}: {
  job: ResearchCoverageJobSummary;
  onCancel: (job: ResearchCoverageJobSummary) => void;
  cancelling: boolean;
  showTenant?: boolean;
}) {
  const [showKeywords, setShowKeywords] = useState(false);
  const jobDetail = useResearchCoverageJob(job.job_id, job.tenant_id, showKeywords);
  const keywordDetails = jobDetail.data?.keywords ?? [];
  const progress = job.keywords_total
    ? Math.round(((job.keywords_done + job.keywords_failed) / job.keywords_total) * 100)
    : 0;

  return (
    <div className="rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {statusBadge(job.status)}
            <span className="font-mono text-xs text-muted-foreground">Ref: {job.job_id}</span>
          </div>
          {showTenant && <div className="mt-1 text-sm font-medium">{job.tenant_id}</div>}
          <div className="text-xs text-muted-foreground">{formatDateTime(job.created_at)}</div>
        </div>
        {isActiveResearchCoverageJobStatus(job.status) && (
          <Button
            variant="outline"
            size="sm"
            disabled={cancelling}
            onClick={() => onCancel(job)}
          >
            Cancel
          </Button>
        )}
      </div>
      <div className="mt-3 space-y-2">
        <Progress value={progress} />
        <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-4">
          <span>{job.keywords_done}/{job.keywords_total} keywords searched</span>
          {job.keywords_failed > 0 && <span>{job.keywords_failed} couldn't be searched</span>}
          <span>{job.rows_upserted} sources found</span>
          <span>{job.usable_rows} approved</span>
        </div>
      </div>
      {job.keywords_total > 0 && (
        <div className="mt-3 border-t pt-2">
          <button
            type="button"
            onClick={() => setShowKeywords((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            {showKeywords ? 'Hide' : 'Show'} per-keyword results ({keywordDetails.length || job.keywords_total})
          </button>
          {showKeywords && (
            <div className="mt-2 space-y-1">
              {jobDetail.isLoading ? (
                <div className="flex items-center gap-2 rounded border p-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading per-keyword results…
                </div>
              ) : jobDetail.isError ? (
                <div className="rounded border border-destructive/20 p-2 text-xs text-destructive">
                  {getResearchCoverageErrorMessage(jobDetail.error)}
                </div>
              ) : keywordDetails.length ? (
                keywordDetails.map((kw, index) => (
                  <div
                    key={`${kw.keyword}:${kw.run_id ?? index}`}
                    className="flex items-center justify-between gap-2 rounded border p-2 text-xs"
                  >
                    <span className="min-w-0 truncate font-medium">{kw.keyword}</span>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                        {keywordStatusLabel(kw.status)}
                      </span>
                      {kw.error && (
                        <span className="text-destructive" title={kw.error}>
                          Error
                        </span>
                      )}
                      <span className="text-muted-foreground">{kw.usable_rows} approved</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded border p-2 text-xs text-muted-foreground">
                  No results yet for these keywords.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ClientResearchResults({
  tenantId,
  onResearchKeyword,
}: {
  tenantId: string;
  onResearchKeyword: (keyword: string) => void;
}) {
  const pools = useResearchCoveragePools(tenantId);
  const imports = useResearchCoverageImports(tenantId, 10);
  const keywordRows = useMemo(() => {
    return [...(pools.data?.pools ?? [])].sort((a, b) => {
      const statusRank = { missing: 0, thin: 1, ready: 2 };
      const rankDelta = statusRank[getPoolStatus(a)] - statusRank[getPoolStatus(b)];
      if (rankDelta !== 0) return rankDelta;
      return a.keyword.localeCompare(b.keyword);
    });
  }, [pools.data?.pools]);

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.6fr)]">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-4 w-4 text-teal" />
            Keyword Coverage for {tenantId}
          </CardTitle>
          <CardDescription>
            Keywords with thin or missing approved sources appear first.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {pools.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading keyword coverage…
            </div>
          ) : pools.isError ? (
            <div className="text-sm text-destructive">{getResearchCoverageErrorMessage(pools.error)}</div>
          ) : keywordRows.length ? (
            keywordRows.slice(0, 12).map((pool) => (
              <div
                key={`${pool.tenant_id}:${pool.keyword}`}
                className="flex flex-col gap-3 rounded border p-3 text-sm md:flex-row md:items-center md:justify-between"
              >
                <div className="min-w-0 space-y-1">
                  <div className="truncate font-medium">{pool.keyword}</div>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span>{pool.usable_rows} approved</span>
                    <span>{pool.total_rows} found</span>
                    <span>{pool.distinct_runs} run{pool.distinct_runs === 1 ? '' : 's'}</span>
                    <span>Updated {formatDateTime(pool.last_import_at)}</span>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {poolStatusBadge(pool)}
                  {getPoolStatus(pool) !== 'ready' && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => onResearchKeyword(pool.keyword)}
                    >
                      Find sources
                    </Button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No keyword coverage for {tenantId} yet.
            </div>
          )}
          {keywordRows.length > 12 && (
            <p className="pt-1 text-xs text-muted-foreground">
              Showing 12 keywords that need the most attention first.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recently Added for {tenantId}</CardTitle>
          <CardDescription>Newest approved-source batches for this workspace.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {imports.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading recent sources…
            </div>
          ) : imports.isError ? (
            <div className="text-sm text-destructive">{getResearchCoverageErrorMessage(imports.error)}</div>
          ) : (imports.data?.imports ?? []).length ? (
            (imports.data?.imports ?? []).slice(0, 8).map((row) => (
              <div key={row.id} className="rounded border p-2 text-sm">
                <div className="truncate font-medium">{row.keyword}</div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <span>{formatDateTime(row.timestamp)}</span>
                  <span>{row.usable_count} approved</span>
                  <span>{row.rows} found</span>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No recently added sources for {tenantId}.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function NoClientSelected() {
  return (
    <div className="rounded-md border border-dashed p-8 text-center">
      <h2 className="text-lg font-semibold">Pick a client first</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Use the workspace selector in the top bar so this page can show client-specific research coverage.
      </p>
    </div>
  );
}

export default function ResearchCoveragePage() {
  const currentTenant = useTenantStore((state) => state.currentTenant);
  const selectedTenant = currentTenant?.trim().toLowerCase() ?? '';
  const [keywordRequest, setKeywordRequest] = useState<KeywordResearchRequest | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  function handleResearchKeyword(keyword: string) {
    setKeywordRequest((prev) => ({ id: (prev?.id ?? 0) + 1, keyword }));
    requestAnimationFrame(() => {
      triggerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  return (
    <ElevatedGuard>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Research Coverage</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Trusted sources backing content for the selected client.
          </p>
          {selectedTenant && (
            <div className="mt-3 inline-flex rounded-full border bg-muted/30 px-3 py-1 text-xs font-medium">
              Client: {selectedTenant}
            </div>
          )}
        </div>
        {selectedTenant ? (
          <>
            <ClientSummaryCards tenantId={selectedTenant} />
            <div ref={triggerRef}>
              <TriggerCard
                key={selectedTenant}
                tenantId={selectedTenant}
                keywordRequest={keywordRequest}
              />
            </div>
            <ClientResearchResults
              tenantId={selectedTenant}
              onResearchKeyword={handleResearchKeyword}
            />
            <JobsCard
              tenantId={selectedTenant}
              title={`Research Activity for ${selectedTenant}`}
              description="Recent source-finding requests for this workspace."
              showTenant={false}
            />
          </>
        ) : (
          <NoClientSelected />
        )}
      </div>
    </ElevatedGuard>
  );
}
